import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';

import { getSession, onAuthStateChange } from '@/lib/api';

/**
 * Cart store.
 *
 * Invariantes históricos:
 *   1. `idempotencyKey` UUID v4 generado **una vez** al iniciar checkout y
 *      persistido a localStorage. Se mantiene durante todo el ciclo del
 *      pago hasta resolver (confirmed | failed). Sobrevive hard reloads.
 *
 *   2. El store NUNCA cachea precios — vienen del server en cada
 *      `/event/:id` y en el resumen. El cliente sólo trackea selección.
 *
 * Cambios ROUND21:
 *
 *   - **#2 multi-event tickets** — `CartLine` ahora incluye `eventId`. Una
 *     misma sesión puede acumular boletas de varios eventos distintos. El
 *     pointer global `eventId` queda como "evento que el user está
 *     navegando" y NO se usa más como filtro de líneas (un `setEvent`
 *     anterior reseteaba `lines`, lo cual destruía boletas previas de otro
 *     evento).
 *
 *   - **#7 persistir cart entre sesiones del MISMO user** — el listener de
 *     auth ya no escribe a localStorage en `SIGNED_OUT`. Antes (R20)
 *     vaciaba la key del user, lo cual significaba que el mismo user al
 *     volver a loguear veía cart vacío. Ahora SIGNED_OUT sólo limpia
 *     memoria sin tocar storage — la próxima vez que el user logue se
 *     rehidrata de su key intacta.
 *
 *     El P0 cross-user de R20 sigue cubierto: cada user tiene su propia key
 *     (`fc-commerce-cart-<user.id|anon>`); user B nunca puede ver la de A.
 */
export type CartLine = {
  /**
   * ROUND21 #2 — event al que pertenece la línea. Permite que el cart
   * acumule boletas de eventos distintos sin que addLine sobrescriba.
   * Null sólo en líneas seedeadas pre-R21 (toleradas por compatibilidad).
   */
  eventId: string | null;
  stageId: string;
  localityId: string | null;
  seatIdentifier: string | null;
  quantity: number;
  /**
   * F5 — compra por silla: nombre del titular del asiento (QR nominal). Sólo
   * aplica a líneas con `seatIdentifier` != null. Se captura en el carrito
   * (CartReview) antes del checkout y viaja en `holder_names` a la orden.
   */
  holderName?: string | null;
};

/**
 * ROUND7 P1 — Product line (partners store). Distinct from event ticket lines:
 * doesn't lock seats, doesn't expire mid-checkout (TTL handled by cart-level
 * expires_at if applicable).
 */
export type ProductLine = {
  productId: string;
  partnerId: string;
  partnerName: string;
  title: string;
  unitPriceCents: number;
  quantity: number;
};

export type CartLockSnapshot = {
  orderId: string;
  expiresAt: string;
  lockedAt: string;
};

export type CartState = {
  /** Last event the user navigated to. Used only as "current event"
   *  pointer for UX (volver al evento, ver carrito); NO filtra `lines`. */
  eventId: string | null;
  lines: CartLine[];
  /** ROUND7 P1 — Partners store products. Independent del flow event_lock. */
  productLines: ProductLine[];
  idempotencyKey: string | null;
  /** Set after a successful POST /cart/lock. Cleared on finalize/reset. */
  lock: CartLockSnapshot | null;
  reset: () => void;
  /**
   * ROUND19 #2 — alias semántico de `reset()` para la acción "Vaciar
   * carrito" del UI. ROUND21 #7: deja de usarse para limpiar en signOut
   * (eso queda en mano del listener de auth, que ya no escribe a storage).
   */
  clearCart: () => void;
  setEvent: (eventId: string) => void;
  addLine: (line: CartLine) => void;
  removeLine: (index: number) => void;
  updateLineQuantity: (index: number, quantity: number) => void;
  /** F5 — set holder name for a per-seat line (index in `lines`). */
  updateLineHolder: (index: number, holderName: string) => void;
  /** Add product line (partner store). Dedupes by productId. */
  addProductLine: (line: ProductLine) => void;
  removeProductLine: (productId: string) => void;
  updateProductLineQuantity: (productId: string, quantity: number) => void;
  beginCheckout: () => string;
  applyLock: (input: { orderId: string; expiresAt: string }) => void;
  finalizeCheckout: () => void;
  /** Selector: total quantity across event lines. */
  totalQuantity: () => number;
  /** Selector: total quantity across product lines. */
  totalProductQuantity: () => number;
  /** Selector: returns true if `lock?.expiresAt` is in the past. */
  isLockExpired: () => boolean;
  /** ROUND21 #2 — set de eventIds únicos presentes en `lines`. */
  uniqueEventIds: () => string[];
};

/**
 * ROUND20/ROUND21 — Scoped storage adapter (P0 cross-user leak fix).
 *
 * Cada IO computa la key real `fc-commerce-cart-<user.id|anon>` en vivo
 * contra `currentUserId`. Sin esto, todos los users compartirían la misma
 * key fija y verían el cart del último que estuvo en el browser.
 *
 * ROUND21 #7 — el listener de abajo NO escribe a storage en SIGNED_OUT.
 * Sólo limpia memoria, así la key del user (con su cart) sobrevive para
 * el próximo login del MISMO user.
 */
let currentUserId: string | null = null;

const SCOPED_KEY_PREFIX = 'fc-commerce-cart';

function scopedKey(): string {
  return `${SCOPED_KEY_PREFIX}-${currentUserId ?? 'anon'}`;
}

const scopedStorage: StateStorage = {
  getItem: (_name) => {
    try {
      return localStorage.getItem(scopedKey());
    } catch {
      return null;
    }
  },
  setItem: (_name, value) => {
    try {
      localStorage.setItem(scopedKey(), value);
    } catch {
      /* quota / private mode — cart degrada a sólo-memoria */
    }
  },
  removeItem: (_name) => {
    try {
      localStorage.removeItem(scopedKey());
    } catch {
      /* */
    }
  },
};

/**
 * No-op storage usado para SUSPENDER el persist durante transiciones de
 * auth. Sin esto, los `setState` de limpieza ejecutados en SIGNED_OUT
 * sobreescribirían la localStorage key del user con `{empty}`, lo cual
 * rompe el R21 #7 (persistir cart para próximo login del mismo user).
 */
const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => {
    /* deliberate no-op */
  },
  removeItem: () => {
    /* deliberate no-op */
  },
};

function uuidV4(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function emptyCartState(): Pick<
  CartState,
  'eventId' | 'lines' | 'productLines' | 'idempotencyKey' | 'lock'
> {
  return {
    eventId: null,
    lines: [],
    productLines: [],
    idempotencyKey: null,
    lock: null,
  };
}

export const useCart = create<CartState>()(
  persist(
    (set, get) => ({
      eventId: null,
      lines: [],
      productLines: [],
      idempotencyKey: null,
      lock: null,
      reset: () => set(emptyCartState()),
      clearCart: () => set(emptyCartState()),
      setEvent: (eventId) => {
        // ROUND21 #2 — el setEvent es sólo un pointer de UX ("evento que el
        // user está navegando"). NO toca `lines` — éstas pueden venir de
        // varios eventos a la vez.
        set({ eventId });
      },
      addLine: (line) => {
        // ROUND21 #2 — matching incluye `eventId` para que la misma
        // localidad de eventos distintos NO colapse en una sola línea.
        const lines = get().lines;
        const matchIdx = lines.findIndex(
          (l) =>
            l.eventId === line.eventId &&
            l.stageId === line.stageId &&
            l.localityId === line.localityId &&
            l.seatIdentifier === line.seatIdentifier,
        );
        if (matchIdx >= 0) {
          const merged = lines.map((l, i) =>
            i === matchIdx ? { ...l, quantity: l.quantity + line.quantity } : l,
          );
          set({ lines: merged });
          return;
        }
        set({ lines: [...lines, line] });
      },
      removeLine: (index) => set({ lines: get().lines.filter((_, i) => i !== index) }),
      updateLineQuantity: (index, quantity) => {
        if (quantity <= 0) {
          set({ lines: get().lines.filter((_, i) => i !== index) });
          return;
        }
        set({
          lines: get().lines.map((l, i) => (i === index ? { ...l, quantity } : l)),
        });
      },
      updateLineHolder: (index, holderName) => {
        set({
          lines: get().lines.map((l, i) => (i === index ? { ...l, holderName } : l)),
        });
      },
      beginCheckout: () => {
        const existing = get().idempotencyKey;
        if (existing) return existing;
        const key = uuidV4();
        set({ idempotencyKey: key });
        return key;
      },
      addProductLine: (line) => {
        const existing = get().productLines;
        const matchIdx = existing.findIndex((l) => l.productId === line.productId);
        if (matchIdx >= 0) {
          set({
            productLines: existing.map((l, i) =>
              i === matchIdx ? { ...l, quantity: l.quantity + line.quantity } : l,
            ),
          });
          return;
        }
        set({ productLines: [...existing, line] });
      },
      removeProductLine: (productId) =>
        set({ productLines: get().productLines.filter((l) => l.productId !== productId) }),
      updateProductLineQuantity: (productId, quantity) => {
        if (quantity <= 0) {
          set({ productLines: get().productLines.filter((l) => l.productId !== productId) });
          return;
        }
        set({
          productLines: get().productLines.map((l) =>
            l.productId === productId ? { ...l, quantity } : l,
          ),
        });
      },
      applyLock: ({ orderId, expiresAt }) =>
        set({ lock: { orderId, expiresAt, lockedAt: new Date().toISOString() } }),
      finalizeCheckout: () =>
        // ROUND21 — limpiamos sólo el lock + idempotencyKey (boletas se
        // borraron al moverlas a la orden) pero respetamos `eventId` como
        // pointer del último evento navegado.
        set({ lines: [], idempotencyKey: null, lock: null }),
      totalQuantity: () => get().lines.reduce((sum, l) => sum + l.quantity, 0),
      totalProductQuantity: () => get().productLines.reduce((sum, l) => sum + l.quantity, 0),
      isLockExpired: () => {
        const lock = get().lock;
        if (!lock) return false;
        return new Date(lock.expiresAt).getTime() <= Date.now();
      },
      uniqueEventIds: () => {
        const seen = new Set<string>();
        for (const l of get().lines) {
          if (l.eventId) seen.add(l.eventId);
        }
        return [...seen];
      },
    }),
    {
      name: SCOPED_KEY_PREFIX,
      storage: createJSONStorage(() => scopedStorage),
      partialize: (state) => ({
        eventId: state.eventId,
        lines: state.lines,
        productLines: state.productLines,
        idempotencyKey: state.idempotencyKey,
        lock: state.lock,
      }),
      // ROUND21 #2 — bump version: el shape de `CartLine` ahora incluye
      // `eventId`. Cualquier cart persistido pre-R21 pierde el campo →
      // `eventId: null` por compat. El backend (Pagar Todo / lockCart) lo
      // tolera porque sólo usa `stage_inventory_id` + qty.
      version: 1,
      migrate: (persisted, _version) => {
        const p = (persisted as Partial<CartState> | null) ?? {};
        return {
          ...p,
          lines: Array.isArray(p.lines)
            ? p.lines.map((l) => ({
                eventId: (l as CartLine).eventId ?? null,
                stageId: (l as CartLine).stageId,
                localityId: (l as CartLine).localityId ?? null,
                seatIdentifier: (l as CartLine).seatIdentifier ?? null,
                quantity: (l as CartLine).quantity ?? 1,
                holderName: (l as CartLine).holderName ?? null,
              }))
            : [],
        } as CartState;
      },
      // ROUND35 FC-024 — Race protection: si el `getSession()` async
      // resuelve y dispara `rehydrate()` DESPUÉS de que el usuario ya
      // agregó items al cart (en la ventana entre mount y auth resolved),
      // el merge default `{...currentState, ...storedState}` REEMPLAZARÍA
      // los items in-memory con la storage vacía del nuevo user. Custom
      // merge: si la storage no trae lines o productLines, preservamos los
      // que están en memoria. Si trae datos (returning user), gana storage.
      merge: (persistedState, currentState) => {
        const p = (persistedState as Partial<CartState> | null) ?? {};
        const persistedHasLines = Array.isArray(p.lines) && p.lines.length > 0;
        const persistedHasProducts = Array.isArray(p.productLines) && p.productLines.length > 0;
        return {
          ...currentState,
          ...p,
          lines: persistedHasLines ? (p.lines as CartLine[]) : currentState.lines,
          productLines: persistedHasProducts
            ? (p.productLines as ProductLine[])
            : currentState.productLines,
          // idempotencyKey y lock son específicos al checkout en curso —
          // si el storage no los trae, conservamos los in-memory.
          idempotencyKey: p.idempotencyKey ?? currentState.idempotencyKey ?? null,
          lock: p.lock ?? currentState.lock ?? null,
        } as CartState;
      },
    },
  ),
);

/**
 * ROUND20/ROUND21 — Listener de auth integrado.
 *
 * Reglas:
 *
 *   - **SIGNED_OUT** (logout, expire, signOut desde otra tab):
 *       1. Suspender persist (noopStorage).
 *       2. Vaciar in-memory state (NO escribe a storage gracias al noop).
 *       3. Restaurar scopedStorage para la próxima escritura.
 *       4. Switchear `currentUserId` a null.
 *     Resultado: la key `fc-commerce-cart-<user.id>` queda INTACTA en
 *     localStorage. Próximo login del MISMO user → rehidrata su cart.
 *     (R21 #7)
 *
 *   - **SIGNED_IN con un user.id distinto al previo** (caso A → B o boot
 *     desde session viva):
 *       1. Suspender persist (noopStorage).
 *       2. Vaciar in-memory state (no clobber de la storage del previo).
 *       3. Restaurar scopedStorage.
 *       4. Switchear `currentUserId` al nuevo.
 *       5. Rehidratar desde la key del nuevo user (puede estar vacía).
 *     Resultado: B nunca ve datos de A en memoria, y la key de A queda
 *     intacta para cuando A vuelva. (R20 cross-user no-leak + R21 #7)
 *
 *   - **SIGNED_IN con mismo user (TOKEN_REFRESHED, USER_UPDATED, etc.)**:
 *     no-op.
 *
 * Se arma una sola vez por bundle (idempotente vía flag).
 */
let cartUserScopeArmed = false;
function armCartUserScopeListener(): void {
  if (cartUserScopeArmed) return;
  cartUserScopeArmed = true;

  function suspendPersist(): void {
    useCart.persist.setOptions({
      storage: createJSONStorage(() => noopStorage),
    });
  }
  function resumePersist(): void {
    useCart.persist.setOptions({
      storage: createJSONStorage(() => scopedStorage),
    });
  }

  // Boot: leer sesión existente sin clobber. Si ya hay sesión viva
  // (ej. reload con token vivo), switchear key + rehidratar.
  {
    const boot = getSession();
    const newId = boot?.user?.id ?? null;
    if (newId !== currentUserId) {
      currentUserId = newId;
      void useCart.persist.rehydrate();
    }
  }

  onAuthStateChange((event, session) => {
    const newId = session?.user?.id ?? null;

    // Caso SIGNED_OUT: limpiamos memoria sin tocar storage del user previo.
    if (event === 'SIGNED_OUT') {
      suspendPersist();
      useCart.setState(emptyCartState());
      resumePersist();
      currentUserId = null;
      return;
    }

    // Caso switch de user (incluido SIGNED_IN desde estado anon o desde
    // otro user.id distinto). Si es el MISMO user, salimos.
    if (newId === currentUserId) return;

    suspendPersist();
    useCart.setState(emptyCartState());
    resumePersist();
    currentUserId = newId;
    void useCart.persist.rehydrate();
  });
}
armCartUserScopeListener();
